/**
 * t443u: Vehicles and Journeys on the phone (phone web and the iPhone app share this). Who sees them, how the lists
 * are grouped and worded, and the two create forms (Add vehicle, Plan a journey) with their checks and request bodies,
 * so both apps send exactly what the desktop sends. Pure TS.
 */
import { stoppedLabel } from '../fleet/tracking';
import { DateTime } from 'luxon';
import type { DriverOption, Journey, JourneyStatus, JourneyStop, LicenceCategory, Vehicle, VehicleType, FuelType } from '../fleet/types';
import { formatReg, normaliseReg, VEHICLE_TYPES } from '../fleet/logic';
import { defaultDeparture } from '../fleet/planner';
import { formatInstantGB, groupThousands } from '../gbDate';

type Holds = (perm: string) => boolean;
const ZONE = 'Europe/London';

// ------------------------------------------------------------------------------------------------ who sees what
/** The vehicle list is open to fleet viewers and to whoever runs the vehicles (the API's own rule). */
export const FLEET_VEHICLE_PERMS = ['fleet.view', 'fleet.vehicles.manage'] as const;
/** The journey list (everyone's journeys) needs fleet.view; drivers have My runs instead. */
export const FLEET_JOURNEY_PERMS = ['fleet.view'] as const;
/** t541u: Fleet > Live map (vehicle locations during journeys), for planners and anyone given tracking. */
export const FLEET_TRACKING_PERMS = ['fleet.tracking.view', 'fleet.journeys.plan'] as const;
export interface PhoneFleetAccess { vehicles: boolean; journeys: boolean; addVehicle: boolean; planJourney: boolean }
/** `holds` is "holds this permission at any building". */
export function phoneFleetAccess(holds: Holds): PhoneFleetAccess {
  return {
    vehicles: FLEET_VEHICLE_PERMS.some(holds), journeys: FLEET_JOURNEY_PERMS.some(holds),
    addVehicle: holds('fleet.vehicles.manage'), planJourney: holds('fleet.journeys.plan'),
  };
}

// ------------------------------------------------------------------------------------------------ vehicles
/** Short type names for rows (the desktop's); the form offers VEHICLE_TYPES' longer ones. */
export const VEHICLE_TYPE_LABEL: Record<VehicleType, string> = { car: 'Car', van: 'Van', luton: 'Luton van', truck_7_5t: '7.5t truck', hgv: 'HGV' };
export const VEHICLE_TYPE_OPTIONS = VEHICLE_TYPES.map(t => ({ id: t.key, name: t.label }));
export const FUEL_LABEL: Record<FuelType, string> = { diesel: 'Diesel', petrol: 'Petrol', electric: 'Electric', hybrid: 'Hybrid' };
export const FUEL_OPTIONS = (Object.keys(FUEL_LABEL) as FuelType[]).map(k => ({ id: k, name: FUEL_LABEL[k] }));
const ITEM_LABEL: Record<string, string> = { mot: 'MOT', tax: 'Tax', insurance: 'Insurance', service: 'Service', tacho: 'Tacho' };

/** The registration as it is printed on the plate ("AB12 CDE"). */
export const plateText = (reg: string | null | undefined) => (reg ? formatReg(reg) : '');

/** The one compliance line a row shows: the worst overdue or due-soon item, else nothing. */
export function vehicleAttention(v: Pick<Vehicle, 'compliance' | 'status' | 'off_road_reason'>): string | null {
  if (v.status === 'off_road') return v.off_road_reason ? `Off road: ${v.off_road_reason}` : 'Off road';
  if (v.status === 'retired') return 'Retired';
  const items = (v.compliance ?? []).filter(c => c.state === 'overdue' || c.state === 'due_soon')
    .sort((a, b) => (a.days_left ?? 0) - (b.days_left ?? 0));
  const c = items[0];
  if (!c) return null;
  const label = ITEM_LABEL[c.item] ?? c.item;
  if (c.state === 'overdue') return `${label} overdue`;
  return c.days_left === 0 ? `${label} due today` : c.days_left === 1 ? `${label} due tomorrow` : `${label} due in ${c.days_left} days`;
}

/** Row text: plate, then "Name · Luton van · Warehouse", then the attention line (if any). */
export function vehicleRow(v: Vehicle): { title: string; lines: string[]; attention: string | null } {
  const sub = [v.name, v.make_model && v.make_model !== v.name ? v.make_model : null, VEHICLE_TYPE_LABEL[v.vehicle_type], v.home_location_name].filter(Boolean) as string[];
  return { title: plateText(v.reg), lines: sub.length ? [sub.join(' · ')] : [], attention: vehicleAttention(v) };
}

/** Search by plate (spaces ignored), name, make or home building; off-road ones first, then by plate. */
export function filterVehicles(list: Vehicle[], q: string): Vehicle[] {
  const s = q.trim().toLowerCase(), plate = normaliseReg(q);
  const hit = (v: Vehicle) => !s || (plate.length > 0 && normaliseReg(v.reg).includes(plate))
    || [v.name, v.make_model, v.home_location_name].some(x => x?.toLowerCase().includes(s));
  const rank = (v: Vehicle) => (v.status === 'off_road' ? 0 : v.status === 'active' ? 1 : 2);
  return list.filter(hit).sort((a, b) => rank(a) - rank(b) || a.reg.localeCompare(b.reg));
}

/** The vehicle's detail lines (label, value), in the desktop's order; empty ones are left out. */
export function vehicleFacts(v: Vehicle, fmtDate: (iso: string) => string): [string, string][] {
  const out: [string, string | null | undefined][] = [
    ['Name', v.name], ['Make and model', v.make_model], ['Type', VEHICLE_TYPE_LABEL[v.vehicle_type]], ['Fuel', FUEL_LABEL[v.fuel_type]],
    ['Home building', v.home_location_name], ['Pallets', v.pallets != null ? String(v.pallets) : null],
    ['Payload', v.payload_kg != null ? `${groupThousands(v.payload_kg)} kg` : null],
    ['Odometer', v.odometer_miles != null ? `${groupThousands(v.odometer_miles)} miles` : null],
    ...(v.compliance ?? []).filter(c => c.state !== 'not_required').map(c => [ITEM_LABEL[c.item] ?? c.item, c.due ? `${fmtDate(c.due)}${c.state === 'overdue' ? ' (overdue)' : ''}` : 'Not set'] as [string, string]),
    ['Notes', v.notes],
  ];
  return out.filter((x): x is [string, string] => Boolean(x[1]));
}

export interface VehicleForm { reg: string; name: string; make_model: string; vehicle_type: VehicleType; fuel_type: FuelType; home_location_id: string; pallets: string; mot_due: string; tax_due: string; insurance_due: string }
export const emptyVehicleForm = (home = ''): VehicleForm => ({ reg: '', name: '', make_model: '', vehicle_type: 'van', fuel_type: 'diesel', home_location_id: home, pallets: '', mot_due: '', tax_due: '', insurance_due: '' });
/** The server's own checks, so Save says what is missing before it posts. */
export function vehicleProblem(f: VehicleForm): string | null {
  if (!normaliseReg(f.reg)) return 'Enter the registration';
  if (!/^[A-Z0-9]{2,8}$/.test(normaliseReg(f.reg))) return 'Enter a valid registration';
  if (!f.home_location_id) return 'Choose a home building';
  if (f.pallets.trim() && !/^\d{1,3}$/.test(f.pallets.trim())) return 'Pallets must be a whole number';
  return null;
}
/** POST /fleet/vehicles body (blank fields left out, so the server's defaults apply). */
export function vehiclePayload(f: VehicleForm): Partial<Vehicle> {
  const b: Partial<Vehicle> = { reg: normaliseReg(f.reg), vehicle_type: f.vehicle_type, fuel_type: f.fuel_type, home_location_id: f.home_location_id };
  if (f.name.trim()) b.name = f.name.trim();
  if (f.make_model.trim()) b.make_model = f.make_model.trim();
  if (f.pallets.trim()) b.pallets = Number(f.pallets.trim());
  for (const k of ['mot_due', 'tax_due', 'insurance_due'] as const) if (f[k]) b[k] = f[k];
  return b;
}
/**
 * t564u: Edit vehicle on the phone: the same fields as the desktop's Edit dialog (status has its own Off road button).
 * Numbers and dates are text while typing; blank clears a field.
 */
export interface VehicleEditForm extends VehicleForm {
  payload_kg: string; odometer_miles: string; required_licence: LicenceCategory; service_due: string; tacho_due: string; co2_g_per_km: string; notes: string;
}
export const VEHICLE_EDIT_NUMBERS = { pallets: [0, 40, 'Pallets'], payload_kg: [0, 44000, 'Payload'], odometer_miles: [0, 2_000_000, 'Odometer'], co2_g_per_km: [0, 2000, 'CO₂'] } as const;
const VEHICLE_EDIT_DATES = ['mot_due', 'tax_due', 'insurance_due', 'service_due', 'tacho_due'] as const;
const VEHICLE_EDIT_TEXT = ['name', 'make_model', 'notes'] as const;
export function vehicleEditForm(v: Vehicle): VehicleEditForm {
  const n = (x: number | null | undefined) => (x == null ? '' : String(x));
  return {
    reg: plateText(v.reg), name: v.name ?? '', make_model: v.make_model ?? '', vehicle_type: v.vehicle_type, fuel_type: v.fuel_type, home_location_id: v.home_location_id,
    pallets: n(v.pallets), payload_kg: n(v.payload_kg), odometer_miles: n(v.odometer_miles), co2_g_per_km: n(v.co2_g_per_km), required_licence: v.required_licence,
    mot_due: v.mot_due ?? '', tax_due: v.tax_due ?? '', insurance_due: v.insurance_due ?? '', service_due: v.service_due ?? '', tacho_due: v.tacho_due ?? '', notes: v.notes ?? '',
  };
}
export function vehicleEditProblem(f: VehicleEditForm): string | null {
  const base = vehicleProblem({ ...f, pallets: '' });
  if (base) return base;
  for (const [k, [lo, hi, label]] of Object.entries(VEHICLE_EDIT_NUMBERS) as [keyof typeof VEHICLE_EDIT_NUMBERS, readonly [number, number, string]][]) {
    const t = f[k].trim();
    if (t && (!/^\d+$/.test(t) || Number(t) < lo || Number(t) > hi)) return `${label} must be a whole number up to ${String(hi).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
  for (const k of VEHICLE_EDIT_DATES) if (f[k] && !/^\d{4}-\d{2}-\d{2}$/.test(f[k])) return 'Choose a date';
  if (f.notes.length > 2000) return 'Notes can be up to 2,000 characters';
  return null;
}
/** PATCH /fleet/vehicles/:id body: only what changed (cleared fields are null). */
export function vehicleEditPayload(f: VehicleEditForm, v: Vehicle): Partial<Vehicle> {
  const b: Record<string, unknown> = {};
  const reg = normaliseReg(f.reg);
  if (reg !== normaliseReg(v.reg)) b.reg = reg;
  for (const k of ['vehicle_type', 'fuel_type', 'home_location_id', 'required_licence'] as const) if (f[k] !== v[k]) b[k] = f[k];
  for (const k of VEHICLE_EDIT_TEXT) { const t = f[k].trim() || null; if (t !== (v[k] ?? null)) b[k] = t; }
  for (const k of Object.keys(VEHICLE_EDIT_NUMBERS) as (keyof typeof VEHICLE_EDIT_NUMBERS)[]) { const t = f[k].trim() === '' ? null : Number(f[k].trim()); if (t !== (v[k] ?? null)) b[k] = t; }
  for (const k of VEHICLE_EDIT_DATES) { const t = f[k] || null; if (t !== (v[k] ?? null)) b[k] = t; }
  return b as Partial<Vehicle>;
}
/** Fills the blanks from a DVLA lookup (never overwrites what was typed). */
export function withLookup(f: VehicleForm, l: { make: string | null; fuel: string | null; tax_due: string | null; mot_due: string | null } | null): VehicleForm {
  if (!l) return f;
  const fuel = (l.fuel ?? '').toLowerCase();
  const fuelType: FuelType | null = fuel.includes('electric') ? 'electric' : fuel.includes('hybrid') ? 'hybrid' : fuel.includes('petrol') ? 'petrol' : fuel.includes('diesel') ? 'diesel' : null;
  return { ...f, make_model: f.make_model || l.make || '', mot_due: f.mot_due || l.mot_due || '', tax_due: f.tax_due || l.tax_due || '', fuel_type: fuelType ?? f.fuel_type };
}

// ------------------------------------------------------------------------------------------------ journeys
export const JOURNEY_STATUS_LABEL: Record<JourneyStatus, string> = { planned: 'Planned', assigned: 'Assigned', in_progress: 'On the road', completed: 'Completed', cancelled: 'Cancelled' };
const londonDay = (iso: string | number) => formatInstantGB(new Date(iso), 'yyyy-MM-dd');

/** The phone list asks for journeys from a week ago to two weeks ahead (UK dates; `to` is exclusive). */
export function journeysRange(now: number = Date.now()): { from: string; to: string } {
  const d = DateTime.fromMillis(now).setZone(ZONE).startOf('day');
  return { from: d.minus({ days: 7 }).toISODate()!, to: d.plus({ days: 15 }).toISODate()! };
}

type JItem = Pick<Journey, 'id' | 'status' | 'planned_start'>;
/** On the road first, then today's, then upcoming (soonest first); earlier ones and finished ones last (newest first). */
export function groupJourneys<T extends JItem>(list: T[], now: number = Date.now()): { title: string; items: T[] }[] {
  const day = londonDay(now);
  const asc = (a: T, b: T) => a.planned_start.localeCompare(b.planned_start);
  const live = list.filter(j => j.status === 'in_progress').sort(asc);
  const open = list.filter(j => j.status === 'planned' || j.status === 'assigned');
  const today = open.filter(j => londonDay(j.planned_start) === day).sort(asc);
  const later = open.filter(j => londonDay(j.planned_start) > day).sort(asc);
  const done = list.filter(j => !live.includes(j) && !today.includes(j) && !later.includes(j)).sort((a, b) => asc(b, a));
  return [{ title: 'On the road', items: live }, { title: 'Today', items: today }, { title: 'Coming up', items: later }, { title: 'Earlier', items: done }].filter(g => g.items.length);
}

/** "Tue 27 Oct, 08:00" in UK time. */
export const journeyWhen = (iso: string) => formatInstantGB(new Date(iso), 'EEE d MMM, HH:mm');

/** A stop's muted line: when it's due, or what happened there. */
export function stopLine(s: Pick<JourneyStop, 'status' | 'eta' | 'late'>): string {
  const what = s.status === 'pending' ? (s.eta ? `Due ${formatInstantGB(new Date(s.eta), 'HH:mm')}` : null)
    : s.status === 'completed' ? 'Delivered' : s.status === 'failed' ? 'Not delivered' : 'Arrived';
  return [what, s.late ? 'Late' : null].filter(Boolean).join(' · ');
}

/** Row text: ref, then when · from, then who drives (and progress once it's moving). */
export function journeyRow(j: Journey): { title: string; lines: string[]; status: string } {
  const who = j.kind === 'haulier' ? (j.haulier_name ?? 'Haulier not chosen') : [j.reg ? plateText(j.reg) : null, j.driver_name].filter(Boolean).join(' · ') || 'No vehicle or driver yet';
  const stops = j.stop_count === 1 ? '1 stop' : `${j.stop_count} stops`;
  const progress = j.status === 'in_progress' || j.status === 'completed' ? `${j.done_count} of ${j.stop_count} done${j.late_count ? `, ${j.late_count} late` : ''}` : stops;
  const lines = [[journeyWhen(j.planned_start), j.origin_name].filter(Boolean).join(' · '), `${who} · ${progress}`];
  // t541u: a muted "Stopped 18 min" while it's been still a while away from its stops (never an accusation).
  if (j.status === 'in_progress' && j.stopped) lines.push(stoppedLabel(j.stopped.minutes));
  return { title: j.ref, lines, status: JOURNEY_STATUS_LABEL[j.status] };
}

export function filterJourneys<T extends Journey>(list: T[], q: string): T[] {
  const s = q.trim().toLowerCase(), plate = normaliseReg(q);
  if (!s) return list;
  return list.filter(j => [j.ref, j.origin_name, j.driver_name, j.haulier_name, j.notes].some(x => x?.toLowerCase().includes(s)) || (plate.length > 1 && j.reg != null && normaliseReg(j.reg).includes(plate)));
}

export interface JourneyStopForm { key: string; label: string; address: string | null; lat: number | null; lng: number | null; place_id: string | null; location_id: string | null }
/** vehicle_id / driver_id (t494u): optional, assigned on create exactly like Assign; blank ('') leaves it unassigned. */
export interface JourneyForm { origin_location_id: string; day: string; time: string; kind: 'own' | 'haulier'; notes: string; stops: JourneyStopForm[]; vehicle_id?: string; driver_id?: string }
let stopSeq = 0;
export const newStop = (p: Partial<JourneyStopForm> = {}): JourneyStopForm => ({ key: `s${++stopSeq}`, label: '', address: null, lat: null, lng: null, place_id: null, location_id: null, ...p });
/** Leaves today (UK time) at 08:00, or the next quarter hour once it's past eight (the desktop's default), from the given building, with one empty stop. */
export function emptyJourneyForm(origin = '', now: number = Date.now()): JourneyForm {
  const { day, time } = defaultDeparture(now);
  return { origin_location_id: origin, day, time, kind: 'own', notes: '', stops: [newStop()] };
}
/** The UK wall time as an instant. */
export const ukInstant = (day: string, time: string) => DateTime.fromISO(`${day}T${time}`, { zone: ZONE }).toUTC().toISO()!;
export function journeyProblem(f: JourneyForm): string | null {
  if (!f.origin_location_id) return 'Choose where it leaves from';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.day) || !/^\d{2}:\d{2}$/.test(f.time)) return 'Choose when it leaves';
  if (f.stops.length > 40) return 'Up to 40 stops per journey';
  return null;
}
/** POST /fleet/journeys body: stops without a name are dropped (as on the desktop). */
export function journeyPayload(f: JourneyForm): Record<string, unknown> {
  return {
    kind: f.kind, origin_location_id: f.origin_location_id, planned_start: ukInstant(f.day, f.time), notes: f.notes.trim() || null,
    stops: f.stops.filter(s => s.label.trim()).map(s => ({ label: s.label.trim(), address: s.address, lat: s.lat, lng: s.lng, place_id: s.place_id, location_id: s.location_id })),
    ...(f.kind === 'own' && (f.vehicle_id || f.driver_id) ? { vehicle_id: f.vehicle_id || null, driver_id: f.driver_id || null } : {}),
  };
}

/** One building or address suggestion for a stop: buildings matching the text first, then the map's suggestions. */
export function stopBuildingMatches<T extends { id: string; name: string; address?: string | null }>(buildings: T[], q: string, limit = 3): T[] {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return [];
  return buildings.filter(b => b.name.toLowerCase().includes(s) || b.address?.toLowerCase().includes(s)).slice(0, limit);
}

/** The driver choices in Assign: eligible first, then by name; the muted line says why one can't drive. */
export function driverChoices(opts: DriverOption[]): { id: string; name: string; line: string | null; ok: boolean }[] {
  return [...opts].sort((a, b) => Number(b.eligibility.ok) - Number(a.eligibility.ok) || a.display_name.localeCompare(b.display_name))
    .map(o => ({ id: o.user_id, name: o.display_name, ok: o.eligibility.ok, line: [o.is_external ? 'Self-employed' : null, ...o.eligibility.reasons, ...o.eligibility.warnings].filter(Boolean).join(' · ') || null }));
}
/** Assign is refused when the employee's shift has to grow; the second button retries with extend_shift. */
export const needsShiftExtend = (message: string) => /extend the shift/i.test(message);
